import {createClient} from "@connectrpc/connect";
import {apiTransport} from "../api/connectTransport";
import {
	type CustomDomain as CustomDomainMessage,
	CustomDomainService,
} from "../gen/kitecorp/transferify/storefront/v1/custom_domain_pb";
import {customDomainOf} from "./customDomainMessages";
import type {CustomDomain} from "./customDomain/customDomain";

/**
 * The sender's bring-your-own custom transfer domain (kitecorp/transferify-api-service#348, with the edge
 * lifecycle from kitecorp/devops#126 phase B).
 *
 * <p>Separate from {@link AccountSubdomainApi} because these are different features that happen to share a
 * table and a plan capability: that one claims a label under OUR zone, this one adopts a host the sender
 * owns. Separate from {@link AccountBrandApi} because the gates differ per verb — registering and
 * verifying take the entitlement, while reading and REMOVING stay open on every tier so a downgraded owner
 * can still see and hand back the host they registered.
 *
 * <p>Verification and token rotation are POSTs rather than PUTs because neither is idempotent the way a
 * PUT promises: verification performs a live DNS lookup whose outcome depends on the world, and rotation
 * mints a new secret every time.
 */

/** The domain a write answered with; a write that answers none has nothing to show the section. */
function answeredDomainOf(domain: CustomDomainMessage | undefined): CustomDomain {
	if (domain === undefined) throw new Error("the custom domain answer carries no domain");
	return customDomainOf(domain);
}

const domainClient = () => createClient(CustomDomainService, apiTransport);

export class AccountDomainApi {
	/**
	 * The registered domain, or `null` when there is none: an answer with no `domain`.
	 *
	 * <p>Called on every tier, deliberately. Entitlement says nothing about whether a domain exists, and a
	 * downgraded owner is indistinguishable from a never-entitled one until the api answers, so there is
	 * no narrower condition than "always" that cannot hide a registered host from the only person able to
	 * remove it.
	 */
	static async current(): Promise<CustomDomain | null> {
		const {domain} = await domainClient().getCustomDomain({});
		return domain === undefined ? null : customDomainOf(domain);
	}

	static async register(domain: string): Promise<CustomDomain> {
		return answeredDomainOf((await domainClient().registerCustomDomain({domain})).domain);
	}

	/**
	 * Proves ownership and, on success, provisions the host at the edge.
	 *
	 * <p>One call for both because the api folded provisioning into verification: it is the single retry
	 * affordance, so the same control that proves ownership also recovers a failed provision and there is
	 * never a second button to go looking for.
	 */
	static async verify(): Promise<CustomDomain> {
		return answeredDomainOf((await domainClient().verifyCustomDomain({})).domain);
	}

	/** Issues a fresh challenge token, invalidating the published value and returning the host to pending. */
	static async rotateVerificationToken(): Promise<CustomDomain> {
		return answeredDomainOf((await domainClient().rotateCustomDomainToken({})).domain);
	}

	static async remove(): Promise<void> {
		await domainClient().removeCustomDomain({});
	}
}
